// Sections 11, 15, 16 and 18 (Stream D), 24.14 actions: the education, family and society
// policies, apprenticeships and answers to pairing prompts. Allowed only while deciding (24.7).
// Policy edits are changes relative to the committed state (an unchanged value is dropped from
// the draft); a pairing answer goes into the draft's `eventChoices` ({ kind: 'pairing', approve })
// and is applied by the lifecycle phase at the end of next season.
import type {
  BurialOption,
  ChildLabourOption,
  Decision,
  FestivalOption,
  GameState,
  PairingOption,
  PersonId,
  Policies,
  PolicyId,
  PromptId,
  RetirementAgeOption,
  SchoolSubject,
  SchoolingOption,
  WorkIntensityOption,
} from '../../sim/api';
import { withBoundary } from '../boundary';
import { requireGame } from '../guards';
import { editDraft } from './decision';

function withPolicy<K extends PolicyId>(
  state: GameState,
  d: Decision,
  id: K,
  value: Policies[K],
): Decision {
  const policies = { ...d.policies };
  if (JSON.stringify(state.policies[id]) === JSON.stringify(value)) delete policies[id];
  else policies[id] = value;
  return { ...d, policies };
}

function policyAction<K extends PolicyId>(id: K): (value: Policies[K]) => void {
  return withBoundary((value: Policies[K]): void => {
    const state = requireGame();
    editDraft((d) => withPolicy(state, d, id, value));
  });
}

export const setSchooling: (value: SchoolingOption) => void = policyAction('schooling');
export const setPairingPolicy: (value: PairingOption) => void = policyAction('pairing');
export const setFestivalPolicy: (value: FestivalOption) => void = policyAction('festival');
export const setBurial: (value: BurialOption) => void = policyAction('burial');
export const setRetirementAge: (value: RetirementAgeOption) => void = policyAction('retirementAge');
export const setChildLabour: (value: ChildLabourOption) => void = policyAction('childLabour');
export const setWorkIntensity: (value: WorkIntensityOption) => void = policyAction('workIntensity');

/** Section 18 school subjects: any two different subjects (job skills or Literacy). */
export const setSchoolSubjects = withBoundary(
  (first: SchoolSubject, second: SchoolSubject): void => {
    const state = requireGame();
    editDraft((d) => withPolicy(state, d, 'schoolSubjects', [first, second]));
  },
);

/**
 * Section 11: apprentice `personId` to `masterId` (null ends it). The apprentice must work
 * alongside the master, so they are assigned to the master's job (as drafted) in the same edit.
 */
export const setApprenticeship = withBoundary(
  (personId: PersonId, masterId: PersonId | null): void => {
    const state = requireGame();
    editDraft((d) => {
      const apprenticeships = { ...d.apprenticeships };
      const assignments = { ...d.assignments };
      const current = state.people[personId]?.masterId ?? null;
      if (current === masterId) delete apprenticeships[personId];
      else apprenticeships[personId] = masterId;
      if (masterId !== null) {
        const job = d.assignments[masterId] ?? state.people[masterId]?.job;
        if (job !== undefined && job !== 'rest') {
          if (state.people[personId]?.job === job) delete assignments[personId];
          else assignments[personId] = job;
        }
      }
      return { ...d, apprenticeships, assignments };
    });
  },
);

/** Section 15 pairing policy "player approves": answer an open pairing prompt. */
export const answerPairing = withBoundary((promptId: PromptId, approve: boolean): void => {
  const state = requireGame();
  const prompt = state.openPrompts.find((p) => p.id === promptId);
  if (prompt?.kind !== 'pairing') return;
  editDraft((d) => ({
    ...d,
    eventChoices: { ...d.eventChoices, [promptId]: { kind: 'pairing', approve } },
  }));
});
